/**
 * FinancialVisibilityContext — resolves "may this person see money?" once,
 * live, for the whole app.
 *
 * Inputs (see src/utils/financialVisibility.ts for the policy):
 *   - director?  → `tenant.ownerIds` via useDirectorAccess()
 *   - opt-in?    → `users/{userId}.canViewFinancials`, live via onSnapshot so
 *                  an admin revoking access takes effect without a re-login
 *
 * Side effect: mirrors the resolved answer to localStorage
 * (`cacheCanViewFinancials`) so the SYNC formatters in src/utils/pricing.ts —
 * which have no React context — can mask. That mirror is why this provider
 * must be mounted above every authenticated surface.
 *
 * Fail-closed while resolving: `canViewFinancials` stays false until the
 * user doc lands, so a cold start never flashes real figures at someone who
 * shouldn't see them. `checking` lets a gate show a spinner instead of a
 * premature "no access" screen.
 */
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { useSession } from '@/context/AuthSessionContext';
import { useDirectorAccess } from '@/hooks/useDirectorAccess';
import {
  cacheCanViewFinancials,
  canViewFinancialsSync,
  resolveFinancialVisibility,
  setLiveFinancialVisibility,
} from '@/utils/financialVisibility';

export interface FinancialVisibility {
  /** True when the signed-in user may see currency amounts. */
  canViewFinancials: boolean;
  /**
   * True while the answer isn't authoritative yet (user doc / tenant still
   * loading) AND access isn't already granted. Never true once granted, so a
   * permitted user never sees a spinner flash.
   */
  checking: boolean;
  /** True when access comes from `tenant.ownerIds` rather than the per-user flag. */
  isDirector: boolean;
}

const FinancialVisibilityContext = createContext<FinancialVisibility>({
  canViewFinancials: false,
  checking: false,
  isDirector: false,
});

export function FinancialVisibilityProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { userId } = useSession();
  const { isDirector, checking: directorChecking } = useDirectorAccess();

  // undefined = user doc not read yet; boolean = authoritative flag value.
  // Seeded from the localStorage mirror so a warm reload doesn't re-mask
  // figures for a split second before the snapshot lands.
  const [userFlag, setUserFlag] = useState<boolean | undefined>(() =>
    canViewFinancialsSync() ? true : undefined,
  );

  useEffect(() => {
    if (!userId) {
      setUserFlag(undefined);
      return;
    }
    // Single-doc listener — `getDoc`/`onSnapshot` on one document needs no
    // tenantId filter ("rules are not filters" applies to LIST queries only).
    const unsubscribe = onSnapshot(
      doc(db, 'users', userId),
      (snap) => {
        setUserFlag(snap.exists() ? snap.data()?.canViewFinancials === true : false);
      },
      (err) => {
        // Fail-closed: a listener error leaves money hidden rather than
        // exposed. Directors are unaffected — their access comes from
        // tenant.ownerIds, not this doc.
        console.error('[FinancialVisibility] user listener failed:', err);
        setUserFlag(false);
      },
    );
    return () => unsubscribe();
  }, [userId]);

  const canViewFinancials = resolveFinancialVisibility(isDirector, userFlag);

  // Publish DURING RENDER so the sync formatters in src/utils/pricing.ts are
  // already correct on this same paint — an effect would lag one commit and
  // flash `••••` at a permitted user. Idempotent, so StrictMode is fine.
  setLiveFinancialVisibility(canViewFinancials);

  // Mirror to localStorage too, so the next cold start starts from the
  // last-known-good answer instead of masking until the listener lands.
  useEffect(() => {
    cacheCanViewFinancials(canViewFinancials);
  }, [canViewFinancials]);

  const value = useMemo<FinancialVisibility>(
    () => ({
      canViewFinancials,
      checking:
        !canViewFinancials && (directorChecking || (!!userId && userFlag === undefined)),
      isDirector,
    }),
    [canViewFinancials, directorChecking, isDirector, userId, userFlag],
  );

  return (
    <FinancialVisibilityContext.Provider value={value}>
      {/*
        Keyed so the subtree REMOUNTS whenever the answer flips.

        Most money on screen renders through the sync formatters in
        src/utils/pricing.ts, which read a module-level value — they have no
        way to subscribe, so a component that doesn't consume this context
        would keep showing whatever it rendered the first time. That's fine
        until the answer changes: an admin revoking access mid-session, or
        the first login on a device (where the localStorage seed above can't
        pre-answer) would leave stale figures on screen.

        The flip is rare — at most once at app entry, plus whenever an admin
        actually changes someone's access — and remounting is the only
        approach that covers every render path without a hook at ~200 call
        sites. The cost is losing in-progress component state on that one
        flip, which is the right trade against showing money to someone whose
        access was just revoked.
      */}
      <Fragment key={canViewFinancials ? 'financials-visible' : 'financials-hidden'}>
        {children}
      </Fragment>
    </FinancialVisibilityContext.Provider>
  );
}

/**
 * Whether the signed-in user may see currency amounts.
 *
 * Prefer the `<Money>` component (src/components/money/Money.tsx) for
 * rendering an amount — reach for this hook when you need to hide a whole
 * card, column or nav entry rather than a single figure.
 */
export function useFinancialVisibility(): FinancialVisibility {
  return useContext(FinancialVisibilityContext);
}
